import React, { useState, useEffect } from 'react';
import { Tenant, tenantService } from '../../services/tenantService';
import { useModal } from '../../contexts/ModalContext';
import TenantForm from './TenantForm';
import './TenantList.css';

const TenantList: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [filteredTenants, setFilteredTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleTokens, setVisibleTokens] = useState<Set<number>>(new Set());
  const { showModal } = useModal();

  useEffect(() => {
    loadTenants();
  }, []);

  // Filtrer les tenants basé sur le terme de recherche
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTenants(tenants);
    } else {
      const filtered = tenants.filter(tenant =>
        tenant.nomTenant.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTenants(filtered);
    }
  }, [tenants, searchTerm]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getAllTenants();
      setTenants(data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des tenants');
      console.error('Error loading tenants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (tenantData: { nomTenant: string; chaineConnexion: string }) => {
    try {
      await tenantService.createTenant(tenantData);
      await loadTenants();
      setShowForm(false);
      showModal({
        title: 'Tenant créé',
        message: `Le tenant "${tenantData.nomTenant}" a été créé avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
    } catch (err) {
      showModal({
        title: 'Erreur de création',
        message: 'Erreur lors de la création du tenant. Veuillez réessayer.',
        type: 'error',
        confirmText: 'OK'
      });
      console.error('Error creating tenant:', err);
    }
  };

  const handleUpdate = async (id: number, tenantData: { nomTenant: string; chaineConnexion: string }) => {
    try {
      await tenantService.updateTenant(id, tenantData);
      await loadTenants();
      setEditingTenant(null);
      showModal({
        title: 'Tenant mis à jour',
        message: `Le tenant "${tenantData.nomTenant}" a été mis à jour avec succès.`,
        type: 'success',
        confirmText: 'OK'
      });
    } catch (err) {
      showModal({
        title: 'Erreur de mise à jour',
        message: 'Erreur lors de la mise à jour du tenant. Veuillez réessayer.',
        type: 'error',
        confirmText: 'OK'
      });
      console.error('Error updating tenant:', err);
    }
  };

  const handleDelete = async (id: number) => {
    const tenant = tenants.find(t => t.id === id);
    showModal({
      title: 'Confirmer la suppression',
      message: `Êtes-vous sûr de vouloir supprimer le tenant "${tenant?.nomTenant}" ? Cette action est irréversible.`,
      type: 'confirm',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await tenantService.deleteTenant(id);
          await loadTenants();
          showModal({
            title: 'Tenant supprimé',
            message: 'Le tenant a été supprimé avec succès.',
            type: 'success',
            confirmText: 'OK'
          });
        } catch (err) {
          setError('Erreur lors de la suppression du tenant');
          console.error('Error deleting tenant:', err);
        }
      }
    });
  };

  const handleRegenerateApiKey = async (id: number) => {
    try {
      const result = await tenantService.regenerateApiKey(id);
      showModal({
        title: 'Clé API régénérée',
        message: `Nouvelle clé API générée : ${result.apiKey}`,
        type: 'success',
        confirmText: 'OK'
      });
      await loadTenants();
    } catch (err) {
      setError('Erreur lors de la régénération de la clé API');
      console.error('Error regenerating API key:', err);
    }
  };

  const handleToggleStatus = async (id: number) => {
    const tenant = tenants.find(t => t.id === id);
    const action = tenant?.disabled ? 'activer' : 'désactiver';
    
    showModal({
      title: `Confirmer ${action}`,
      message: `Êtes-vous sûr de vouloir ${action} le tenant "${tenant?.nomTenant}" ?`,
      type: 'confirm',
      confirmText: action === 'activer' ? 'Activer' : 'Désactiver',
      cancelText: 'Annuler',
      onConfirm: async () => {
        try {
          await tenantService.toggleTenantStatus(id);
          await loadTenants();
          showModal({
            title: `Tenant ${action}`,
            message: `Le tenant a été ${action} avec succès.`,
            type: 'success',
            confirmText: 'OK'
          });
        } catch (err) {
          setError('Erreur lors du changement de statut du tenant');
          console.error('Error toggling tenant status:', err);
        }
      }
    });
  };

  const toggleTokenVisibility = (tenantId: number) => {
    const newVisibleTokens = new Set(visibleTokens);
    if (newVisibleTokens.has(tenantId)) {
      newVisibleTokens.delete(tenantId);
    } else {
      newVisibleTokens.add(tenantId);
    }
    setVisibleTokens(newVisibleTokens);
  };

  const maskToken = (token: string | undefined) => {
    if (!token || token.length <= 8) return '••••••••';
    return token.substring(0, 4) + '••••••••' + token.substring(token.length - 4);
  };

  if (loading) {
    return <div className="loading">Chargement des tenants...</div>;
  }

  return (
    <div className="tenant-list">
      <div className="tenant-list-header">
        <h2>Gestion des Tenants</h2>
        <div className="header-actions">
          <div className="search-container">
            <input
              type="text"
              placeholder="Rechercher par nom de tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            Nouveau Tenant
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showForm && (
        <TenantForm
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingTenant && (
        <TenantForm
          tenant={editingTenant}
          onSubmit={(data) => handleUpdate(editingTenant.id, data)}
          onCancel={() => setEditingTenant(null)}
        />
      )}

      <div className="tenant-grid">
        {filteredTenants.map((tenant) => (
          <div key={tenant.id} className="tenant-card">
            <div className="tenant-card-header">
              <h3>{tenant.nomTenant}</h3>
              <div className="tenant-status">
                <span className={`status ${tenant.disabled ? 'disabled' : 'active'}`}>
                  {tenant.disabled ? 'Désactivé' : 'Actif'}
                </span>
              </div>
              <div className="tenant-actions">
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => setEditingTenant(tenant)}
                >
                  Modifier
                </button>
                <button 
                  className="btn btn-sm btn-warning"
                  onClick={() => handleToggleStatus(tenant.id)}
                >
                  {tenant.disabled ? 'Activer' : 'Désactiver'}
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(tenant.id)}
                >
                  Supprimer
                </button>
              </div>
            </div>
            
            <div className="tenant-card-body">
              <div className="tenant-info">
                <label>Clé API :</label>
                <div className="api-key-container">
                  <code className="api-key">
                    {visibleTokens.has(tenant.id) ? tenant.apiKey : maskToken(tenant.apiKey)}
                  </code>
                  <div className="api-key-actions">
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => toggleTokenVisibility(tenant.id)}
                      title={visibleTokens.has(tenant.id) ? "Masquer la clé" : "Afficher la clé"}
                    >
                      {visibleTokens.has(tenant.id) ? '👁️' : '👁️‍🗨️'}
                    </button>
                    <button 
                      className="btn btn-sm btn-outline"
                      onClick={() => handleRegenerateApiKey(tenant.id)}
                      title="Régénérer la clé API"
                    >
                      🔄
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="tenant-info">
                <label>Chaîne de connexion :</label>
                <code className="connection-string">
                  {tenant.chaineConnexion ? tenant.chaineConnexion.substring(0, 50) + '...' : 'Non définie'}
                </code>
              </div>
              
              <div className="tenant-info">
                <label>Date de création :</label>
                <span>{new Date(tenant.dateCreation).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredTenants.length === 0 && (
        <div className="empty-state">
          <p>
            {searchTerm.trim() === '' 
              ? 'Aucun tenant trouvé. Créez votre premier tenant pour commencer.'
              : `Aucun tenant trouvé pour "${searchTerm}".`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default TenantList;

